import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["New", "Reviewed", "Contacted", "Closed"];
const STATUS_BADGE = {
  New: "bg-execution text-white",
  Reviewed: "bg-amber-100 text-amber-900 border border-amber-300",
  Contacted: "bg-blue-100 text-blue-900 border border-blue-300",
  Closed: "bg-muted text-muted-foreground border border-border",
};

export default function AdminContacts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");

  const reload = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", { params });
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q) params.q = q;
    reload(params);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/contacts/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <AdminLayout title="Contact messages" subtitle={`${items.length} message${items.length === 1 ? "" : "s"}`}>
      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}

      <div className="mb-6 flex flex-wrap items-end gap-3" data-testid="contacts-filters">
        <label className="flex-1 min-w-[200px]">
          <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Search</span>
          <input
            className="input-swiss"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, email, message…"
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            data-testid="contacts-search"
          />
        </label>
        <label>
          <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Status</span>
          <select
            className="input-swiss"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="contacts-status-filter"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <button onClick={applyFilters} className="btn-primary" data-testid="contacts-apply-filters">Apply</button>
        <button
          onClick={() => { setQ(""); setStatusFilter(""); reload(); }}
          className="btn-secondary"
          data-testid="contacts-reset-filters"
        >
          Reset
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="contacts-empty">
          No messages yet.
        </div>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="contacts-list">
          {items.map((m) => (
            <li key={m.id} className="border border-border bg-white p-5" data-testid={`contact-card-${m.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-heading text-lg font-semibold">{m.name}</div>
                  <a href={`mailto:${m.email}`} className="text-sm text-execution hover:underline">{m.email}</a>
                  {m.phone && <div className="text-xs text-muted-foreground mt-0.5">{m.phone}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {new Date(m.submitted_at).toLocaleDateString()}
                  </div>
                  {user?.role === "admin" && (
                    <button
                      onClick={() => del(m.id)}
                      className="h-7 w-7 inline-flex items-center justify-center border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                      data-testid={`contact-delete-${m.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
              <div className="mt-4 flex items-center gap-2">
                <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[m.status] || STATUS_BADGE.New}`} data-testid={`contact-status-${m.id}`}>
                  {m.status || "New"}
                </span>
                <select
                  className="text-xs border border-border bg-white px-2 py-1"
                  value={m.status || "New"}
                  onChange={(e) => setStatus(m.id, e.target.value)}
                  data-testid={`contact-status-select-${m.id}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </li>
          ))}
        </ul>
      )}
    </AdminLayout>
  );
}
